/**
 * agents-file.ts — AGENTS.md export/import/sync for lore.
 *
 * Lore owns a clearly delimited section inside the file, bounded by HTML
 * comment markers. Everything outside those markers is preserved verbatim.
 * Each knowledge entry is preceded by a hidden <!-- lore:UUID --> comment so
 * the same entry can be tracked across machines and merge conflicts resolved
 * without duplication.
 */

import {
  existsSync,
  readFileSync,
  writeFileSync,
  mkdirSync,
  statSync,
  unlinkSync,
} from "node:fs";
import { dirname, join } from "node:path";
import { db, ensureProject } from "./db";
import * as ltm from "./ltm";
import {
  serialize,
  inline,
  ul,
  liph,
  strong,
  t,
  root,
  unescapeMarkdown,
} from "./markdown";
import { isHostedMode } from "./hosted";
import { config as loreConfig } from "./config";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const LORE_SECTION_START =
  "<!-- This section is maintained by the coding agent via lore (https://github.com/BYK/loreai) -->";
export const LORE_SECTION_END = "<!-- End lore-managed section -->";

/**
 * All known start-marker variants, ordered newest-first.
 * When we renamed the marker in the past, old files kept the old text.
 * splitFile() matches any of these so it can strip all lore sections
 * regardless of which marker version was used to write them.
 */
const ALL_START_MARKERS = [
  LORE_SECTION_START,
  // Pre-rename URL (BYK/opencode-lore → BYK/loreai).
  "<!-- This section is maintained by the coding agent via lore (https://github.com/BYK/opencode-lore) -->",
  "<!-- This section is auto-maintained by lore (https://github.com/BYK/opencode-lore) -->",
] as const;

/**
 * Filename for the dedicated lore knowledge file. Always at the project root.
 * Unlike the agents file (AGENTS.md / CLAUDE.md), this file is entirely owned
 * by lore — no section markers needed, no non-lore content to preserve.
 */
export const LORE_FILE = ".lore.md";

/**
 * Sentinel value for `agentsFile.path` that enables per-agent auto-selection of
 * the host filename (see `resolveAgentsFileName`).
 */
export const AUTO_AGENTS_FILE = "auto";

/** Candidate host filenames for the managed section when `path` is "auto". */
export const AGENTS_FILE_CANDIDATES = ["AGENTS.md", "CLAUDE.md"] as const;

/**
 * Resolve the configured `agentsFile.path` to a concrete filename.
 *
 * A literal path (e.g. "AGENTS.md", "CLAUDE.md", ".cursor/rules/lore.md") is
 * returned verbatim. The "auto" sentinel resolves per client:
 *   - Claude Code sessions (`isClaudeCode === true`) → "CLAUDE.md" (Claude
 *     Code's canonical memory file);
 *   - other agents (`isClaudeCode === false`) → "AGENTS.md";
 *   - no client hint (startup import / file watcher / CLI) → an existing
 *     "CLAUDE.md" in the project if present, else "AGENTS.md".
 *
 * The idle exporter is the authoritative writer: it passes the session's client
 * identity, so the first export creates the right file and the hint-less
 * read/watch paths then discover it via existing-file detection.
 */
export function resolveAgentsFileName(
  configuredPath: string,
  opts: { isClaudeCode?: boolean; projectPath: string },
): string {
  if (configuredPath !== AUTO_AGENTS_FILE) return configuredPath;
  if (opts.isClaudeCode === true) return "CLAUDE.md";
  if (opts.isClaudeCode === false) return "AGENTS.md";
  return existsSync(join(opts.projectPath, "CLAUDE.md"))
    ? "CLAUDE.md"
    : "AGENTS.md";
}

/**
 * The other "auto"-mode candidate filename, used to strip a stale managed
 * section after the resolved target flips between AGENTS.md and CLAUDE.md.
 * Returns null for a custom (non-candidate) path, so an explicitly configured
 * file's counterpart is never touched.
 */
export function otherAgentsFileCandidate(resolvedName: string): string | null {
  if (resolvedName === "AGENTS.md") return "CLAUDE.md";
  if (resolvedName === "CLAUDE.md") return "AGENTS.md";
  return null;
}

const LORE_FILE_HEADER =
  "<!-- Managed by lore (https://github.com/BYK/loreai) — manual edits are imported on next session. -->";

/** Regex matching a valid UUID (v4 or v7) — 8-4-4-4-12 hex groups. */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** Matches `<!-- lore:UUID -->` tracking markers. */
const MARKER_RE = /^<!--\s*lore:([0-9a-f-]+)\s*-->$/;

// ---------------------------------------------------------------------------
// File cache (kv_meta) — skip redundant import/export work
// ---------------------------------------------------------------------------

type LoreFileCache = {
  /** File mtime (milliseconds) at last processing. */
  mtimeMs: number;
  /** hashSection() of file content at that time. */
  hash: string;
};

const CACHE_PREFIX = "lore_file_cache:";

function getCache(fp: string): LoreFileCache | null {
  const row = db()
    .query("SELECT value FROM kv_meta WHERE key = ?")
    .get(CACHE_PREFIX + fp) as { value: string } | null;
  if (!row) return null;
  try {
    return JSON.parse(row.value);
  } catch {
    return null;
  }
}

function setCache(fp: string, entry: LoreFileCache): void {
  const key = CACHE_PREFIX + fp;
  const value = JSON.stringify(entry);
  db()
    .query(
      "INSERT INTO kv_meta (key, value) VALUES (?, ?) ON CONFLICT(key) DO UPDATE SET value = ?",
    )
    .run(key, value, value);
}

/** Clear the cached mtime/hash for an arbitrary agents/lore file path. */
function clearCache(fp: string): void {
  db()
    .query("DELETE FROM kv_meta WHERE key = ?")
    .run(CACHE_PREFIX + fp);
}

/**
 * Clear the cached mtime/hash for a project's `.lore.md`.
 * Useful in tests or after data wipes to force a full re-check.
 */
export function clearLoreFileCache(projectPath: string): void {
  clearCache(join(projectPath, LORE_FILE));
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ParsedFileEntry = {
  /** UUID from `<!-- lore:UUID -->` marker, or null for hand-written entries. */
  id: string | null;
  category: string;
  title: string;
  content: string;
};

// ---------------------------------------------------------------------------
// Section extraction helpers
// ---------------------------------------------------------------------------

/**
 * Split file content into three parts: before, lore section body, after.
 * Returns null for section body when no lore markers are found.
 *
 * Handles multiple lore sections (from duplication bugs) and all known
 * start-marker variants (old + new text) by:
 * - Collecting every lore section span in the file
 * - Returning `before` = content before the first section
 * - Returning `after`  = content after the last section (all intermediate
 *   sections are discarded)
 * - Returning `section` = body of the first section found (for import
 *   and shouldImport to read the canonical content)
 *
 * This is self-healing: a file with N duplicate sections will be collapsed
 * to exactly one on the next exportToFile() call.
 */
function splitFile(fileContent: string): {
  before: string;
  section: string | null;
  after: string;
} {
  // Collect every lore section span in the file, matching all known
  // start-marker variants (current + historical renamed markers).
  // Each span records: where the section body begins/ends and where the
  // full span (including end-marker) ends.
  type Span = {
    markerStart: number;
    bodyStart: number;
    bodyEnd: number;
    spanEnd: number;
  };
  const spans: Span[] = [];

  let searchFrom = 0;
  while (searchFrom < fileContent.length) {
    // Find the earliest occurrence of any known start marker
    let markerStart = -1;
    let markerLen = 0;
    for (const marker of ALL_START_MARKERS) {
      const idx = fileContent.indexOf(marker, searchFrom);
      if (idx !== -1 && (markerStart === -1 || idx < markerStart)) {
        markerStart = idx;
        markerLen = marker.length;
      }
    }
    if (markerStart === -1) break; // no more start markers

    const bodyStart = markerStart + markerLen;
    const endIdx = fileContent.indexOf(LORE_SECTION_END, bodyStart);
    if (endIdx === -1) {
      // Unclosed section — consume to EOF
      spans.push({
        markerStart,
        bodyStart,
        bodyEnd: fileContent.length,
        spanEnd: fileContent.length,
      });
      break;
    }

    spans.push({
      markerStart,
      bodyStart,
      bodyEnd: endIdx,
      spanEnd: endIdx + LORE_SECTION_END.length,
    });
    searchFrom = endIdx + LORE_SECTION_END.length;
  }

  if (spans.length === 0) {
    return { before: fileContent, section: null, after: "" };
  }

  // before = everything before the first lore section (start marker not included)
  // section = body of the first section (used by shouldImport and importFromFile)
  // after = everything after the LAST lore section's end marker
  // Any intermediate duplicate sections are discarded.
  const before = fileContent.slice(0, spans[0].markerStart);
  const section = fileContent.slice(spans[0].bodyStart, spans[0].bodyEnd);
  const after = fileContent.slice(spans[spans.length - 1].spanEnd);

  return { before, section, after };
}

// ---------------------------------------------------------------------------
// Parse entries from a lore section body (or any markdown block)
// ---------------------------------------------------------------------------

/**
 * Extract ParsedFileEntry objects from a markdown section body.
 * Handles:
 * - `<!-- lore:UUID -->` markers before bullet points  → id set
 * - Bare bullet points without markers                → id null
 * - Category derived from the nearest preceding `### Heading`
 * - Malformed or non-UUID markers                    → id null (hand-written)
 * - Duplicate UUIDs                                  → both returned; caller deduplicates
 */
export function parseEntriesFromSection(section: string): ParsedFileEntry[] {
  const lines = section.split("\n");
  const entries: ParsedFileEntry[] = [];
  let currentCategory = "pattern";
  let pendingId: string | null = null;

  for (const raw of lines) {
    const line = raw.trim();

    // Category heading: ### Decision / ### Gotcha / etc.
    const headingMatch = line.match(/^###\s+(.+)$/);
    if (headingMatch) {
      currentCategory = headingMatch[1].toLowerCase();
      pendingId = null;
      continue;
    }

    // Marker line: <!-- lore:UUID -->
    const markerMatch = line.match(MARKER_RE);
    if (markerMatch) {
      const candidate = markerMatch[1];
      pendingId = UUID_RE.test(candidate) ? candidate : null;
      continue;
    }

    // Bullet entry: * **Title**: Content
    const bulletMatch = line.match(/^\*\s+\*\*(.+?)\*\*:\s*(.+)$/);
    if (bulletMatch) {
      // Unescape remark's markdown escapes (e.g. \< → <, \\ → \).
      // Without this, each export/import cycle doubles the backslash-escapes,
      // exponentially inflating stored content.
      entries.push({
        id: pendingId,
        category: currentCategory,
        title: unescapeMarkdown(bulletMatch[1].trim()),
        content: unescapeMarkdown(bulletMatch[2].trim()),
      });
      pendingId = null; // consume the pending marker
      continue;
    }

    // Any non-matching non-empty line resets the pending marker
    if (line !== "" && !line.startsWith("##") && !line.startsWith("<!--")) {
      pendingId = null;
    }
  }

  return entries;
}

// ---------------------------------------------------------------------------
// Content hash (for change detection)
// ---------------------------------------------------------------------------

function hashSection(section: string): string {
  let h = 0;
  for (let i = 0; i < section.length; i++) {
    h = (Math.imul(31, h) + section.charCodeAt(i)) | 0;
  }
  // Convert to unsigned hex string
  return (h >>> 0).toString(16).padStart(8, "0");
}

// ---------------------------------------------------------------------------
// Build the lore section body from DB entries
// ---------------------------------------------------------------------------

function buildSection(
  projectPath: string,
  preloaded?: ltm.KnowledgeEntry[],
): string {
  // Export only project-specific entries (cross_project=0, project_id = this project).
  // Cross-project entries live in the shared DB on each machine and don't belong
  // in a per-project AGENTS.md — including them would inflate the file with
  // unrelated knowledge from every other project the user has worked on.
  const entries = preloaded ?? ltm.forProject(projectPath, false);
  if (!entries.length) {
    return "\n";
  }

  // Group entries by category, preserving DB order (confidence DESC, updated_at DESC).
  const grouped = new Map<string, typeof entries>();
  for (const e of entries) {
    const group = grouped.get(e.category) ?? [];
    group.push(e);
    grouped.set(e.category, group);
  }

  // Build the section body by iterating entries directly, emitting each entry
  // with its own <!-- lore:UUID --> marker. This avoids the title-based Map
  // deduplication bug where multiple entries with the same title all got the
  // same UUID marker from the last Map.set() winner.
  //
  // Merge-friendliness: entries within each category are sorted alphabetically
  // by title (case-insensitive) so the ordering is deterministic across all
  // machines regardless of DB timestamps.  Blank lines between entries give
  // git unique context lines to anchor changes -- two branches adding entries
  // with different titles insert at different positions and auto-merge.
  const out: string[] = [""];

  // Section heading
  out.push("## Long-term Knowledge");

  for (const [category, items] of [...grouped.entries()].sort((a, b) =>
    a[0].localeCompare(b[0]),
  )) {
    out.push("");
    out.push(`### ${category.charAt(0).toUpperCase() + category.slice(1)}`);
    out.push("");

    // Sort entries alphabetically by title for deterministic, merge-friendly output.
    const sorted = [...items].sort((a, b) =>
      a.title.localeCompare(b.title, undefined, { sensitivity: "base" }),
    );

    for (let i = 0; i < sorted.length; i++) {
      if (i > 0) out.push(""); // blank line between entries for git context
      out.push(`<!-- lore:${sorted[i].logical_id} -->`);
      // Render the bullet using remark serializer for proper markdown escaping.
      // serialize(root(ul([liph(...)]))) produces "* **Title**: content\n".
      // Trim the trailing newline since we join with \n ourselves.
      const bullet = serialize(
        root(
          ul([
            liph(
              strong(inline(sorted[i].title)),
              t(`: ${inline(sorted[i].content)}`),
            ),
          ]),
        ),
      ).trimEnd();
      out.push(bullet);
    }
  }

  out.push("");
  return out.join("\n");
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

/**
 * Write a pointer to `.lore.md` inside the agents file (AGENTS.md / CLAUDE.md),
 * preserving all non-lore content. Also writes `.lore.md` with the actual
 * knowledge entries as a side effect.
 */
export function exportToFile(input: {
  projectPath: string;
  filePath: string;
  entries?: ltm.KnowledgeEntry[];
}): void {
  if (isHostedMode()) return;

  // Write the actual entries to .lore.md first.
  exportLoreFile(input.projectPath, input.entries);

  // Build a pointer section for the agents file instead of full entries.
  const pointerBody =
    "\n## Long-term Knowledge\n\n" +
    "For long-term knowledge entries managed by [lore](https://github.com/BYK/loreai) " +
    "(gotchas, patterns, decisions, architecture), see [`.lore.md`](.lore.md) " +
    "in the project root.\n";
  const newSection = `${LORE_SECTION_START + pointerBody + LORE_SECTION_END}\n`;

  let fileContent = "";
  if (existsSync(input.filePath)) {
    fileContent = readFileSync(input.filePath, "utf8");
  }

  const { before, after } = splitFile(fileContent);

  // Ensure there's a blank line separator before the section when appending
  const prefix = before.trimEnd();
  const prefixWithSep = prefix.length > 0 ? `${prefix}\n\n` : "";
  const suffix = after.trimStart();
  const suffixWithSep = suffix.length > 0 ? `\n${suffix}` : "";

  const result = prefixWithSep + newSection + suffixWithSep;

  mkdirSync(dirname(input.filePath), { recursive: true });
  writeFileSync(input.filePath, result, "utf8");
}

/**
 * Write the full knowledge section inline into the agents file (AGENTS.md /
 * CLAUDE.md), preserving all non-lore content. Use this when `.lore.md` is
 * disabled but the user still wants AGENTS.md to carry project knowledge.
 */
export function exportInlineToAgentsFile(input: {
  projectPath: string;
  filePath: string;
  entries?: ltm.KnowledgeEntry[];
}): void {
  if (isHostedMode()) return;
  if (!loreConfig().agentsFile.enabled) return;

  const sectionBody = buildSection(input.projectPath, input.entries);
  const newSection = `${LORE_SECTION_START}\n${sectionBody}${LORE_SECTION_END}\n`;

  let fileContent = "";
  if (existsSync(input.filePath)) {
    fileContent = readFileSync(input.filePath, "utf8");
  }

  const { before, after } = splitFile(fileContent);

  // Reuse the same layout logic as exportToFile for consistency.
  const prefix = before.trimEnd();
  const prefixWithSep = prefix.length > 0 ? `${prefix}\n\n` : "";
  const suffix = after.trimStart();
  const suffixWithSep = suffix.length > 0 ? `\n${suffix}` : "";

  const result = prefixWithSep + newSection + suffixWithSep;

  // Skip write if content hash matches cached hash (DB state unchanged).
  const hash = hashSection(newSection);
  const cached = getCache(input.filePath);
  if (cached && cached.hash === hash) return;

  try {
    mkdirSync(dirname(input.filePath), { recursive: true });
    writeFileSync(input.filePath, result, "utf8");
    const { mtimeMs } = statSync(input.filePath);
    setCache(input.filePath, { mtimeMs, hash });
  } catch (e: unknown) {
    if ((e as NodeJS.ErrnoException).code === "ENOENT") return;
    throw e;
  }
}

/**
 * Delete `.lore.md` from the project root if it exists. Used by clear commands
 * to prevent stale content from being re-imported when the user has disabled
 * `.lore.md` via `loreFile.enabled = false`.
 */
export function deleteLoreFile(projectPath: string): boolean {
  if (isHostedMode()) return false;
  const fp = join(projectPath, LORE_FILE);
  if (!existsSync(fp)) return false;
  try {
    unlinkSync(fp);
    clearLoreFileCache(projectPath);
    return true;
  } catch {
    return false;
  }
}

/**
 * Strip lore's managed section (pointer or inline knowledge) from an agents
 * file (AGENTS.md / CLAUDE.md), preserving all surrounding user content. Used
 * when a project drops to zero knowledge entries so the file doesn't keep a
 * dangling pointer to a deleted `.lore.md` or a stale inline knowledge list.
 *
 * Returns true if the file was modified (or removed). If, after removing the
 * section, no user content remains, the file is deleted entirely.
 */
export function removeLoreSectionFromFile(filePath: string): boolean {
  if (isHostedMode()) return false;
  if (!existsSync(filePath)) return false;

  const fileContent = readFileSync(filePath, "utf8");
  const { before, after, section } = splitFile(fileContent);
  // No lore section present → nothing to strip.
  if (section === null) return false;

  const remainder = `${before.trimEnd()}\n${after.trimStart()}`.trim();
  try {
    if (remainder.length === 0) {
      // The file was lore-only — remove it entirely.
      unlinkSync(filePath);
    } else {
      writeFileSync(filePath, `${remainder}\n`, "utf8");
    }
    clearCache(filePath);
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// shouldImport
// ---------------------------------------------------------------------------

/**
 * Returns true if the file needs to be imported:
 * - File exists and has never been processed (no lore markers)
 * - File exists and its lore section differs from what lore would currently produce
 */
export function shouldImport(input: {
  projectPath: string;
  filePath: string;
}): boolean {
  if (isHostedMode()) return false;
  if (!existsSync(input.filePath)) return false;

  const fileContent = readFileSync(input.filePath, "utf8");
  const { section } = splitFile(fileContent);

  if (section === null) {
    // No lore markers — this is a hand-written file that hasn't been imported
    return fileContent.trim().length > 0;
  }

  // Compare the file's lore section body against what we'd produce now
  const expected = buildSection(input.projectPath);
  return hashSection(section) !== hashSection(expected);
}

// ---------------------------------------------------------------------------
// Import helpers
// ---------------------------------------------------------------------------

/**
 * Upsert parsed entries into the local DB.
 *
 * Behaviour per entry:
 * - Known UUID (already in DB)  → update content if it changed (manual edit)
 * - Unknown UUID (other machine)→ create with that exact ID
 * - No UUID (hand-written)      → create with a new UUIDv7
 * - Duplicate UUID in same file → first occurrence wins, rest ignored
 */
function _importEntries(entries: ParsedFileEntry[], projectPath: string): void {
  const seenIds = new Set<string>();
  const pid = ensureProject(projectPath); // loop-invariant — resolve once

  for (const entry of entries) {
    if (entry.id !== null) {
      // Deduplicate: if same UUID appears twice in file, first wins
      if (seenIds.has(entry.id)) continue;
      seenIds.add(entry.id);

      // entry.id is the <!-- lore:UUID --> marker, which is the stable logical_id.
      const existing = ltm.getByLogical(entry.id);
      // Security (A2, #823): a marker that resolves to a DIFFERENT project's
      // project-scoped entry is foreign — e.g. a .lore.md copied between projects,
      // or a crafted file carrying another project's UUID. Never overwrite another
      // project's private knowledge; skip it. (Cross-project / global entries are
      // intentionally shared, so they stay updatable.)
      if (
        existing &&
        existing.cross_project === 0 &&
        existing.project_id !== null &&
        existing.project_id !== pid
      ) {
        continue;
      }
      if (existing) {
        // Known entry — update only if content changed (manual edit in file). Pass
        // the resolved current row id (Seer #848): update() resolves it to the
        // logical_id and appends a new version.
        if (existing.content !== entry.content) {
          ltm.update(existing.id, { content: entry.content });
        }
      } else if (ltm.isTombstoned(entry.id)) {
      } else {
        // Unknown UUID — entry came from another machine.
        // Check for a fuzzy title match before creating — prevents duplicates
        // when two machines independently create entries for the same concept
        // with different UUIDs but similar titles.
        const fuzzyMatch = ltm.findFuzzyDuplicate({
          title: entry.title,
          projectId: pid,
        });
        if (fuzzyMatch) {
          // Title-similar entry exists locally — update it, discard foreign UUID
          if (
            fuzzyMatch.title !== entry.title ||
            ltm.get(fuzzyMatch.id)?.content !== entry.content
          ) {
            ltm.update(fuzzyMatch.id, { content: entry.content });
          }
        } else {
          // No workerProviderID/workerModelID — these are user-authored
          // entries imported from .lore.md, not worker-produced.
          ltm.create({
            projectPath,
            category: entry.category,
            title: entry.title,
            content: entry.content,
            scope: "project",
            crossProject: false,
            id: entry.id,
          });
        }
      }
    } else {
      // Hand-written entry — create with a new UUIDv7
      // Check for a near-duplicate by title to avoid double-import on re-runs.
      // Scope to project-only entries (false) — cross-project entries from other
      // projects should not silently suppress a hand-written entry in this project.
      const existing = ltm.forProject(projectPath, false);
      const titleMatch = existing.find(
        (e) => e.title.toLowerCase() === entry.title.toLowerCase(),
      );
      if (!titleMatch) {
        ltm.create({
          projectPath,
          category: entry.category,
          title: entry.title,
          content: entry.content,
          scope: "project",
          crossProject: false,
        });
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Import from agents file (AGENTS.md / CLAUDE.md)
// ---------------------------------------------------------------------------

/**
 * Import knowledge entries from the agents file into the local DB.
 * Used for backward compatibility when `.lore.md` doesn't exist yet.
 */
export function importFromFile(input: {
  projectPath: string;
  filePath: string;
}): void {
  if (isHostedMode()) return;
  if (!existsSync(input.filePath)) return;

  const fileContent = readFileSync(input.filePath, "utf8");
  const { section } = splitFile(fileContent);

  // Determine what to parse:
  // - If lore markers exist: parse ONLY the lore section body (avoid re-importing our own output)
  // - If no markers: parse the full file (first-time hand-written AGENTS.md import)
  const textToParse = section ?? fileContent;

  const fileEntries = parseEntriesFromSection(textToParse);
  if (!fileEntries.length) return;

  _importEntries(fileEntries, input.projectPath);
}

// ---------------------------------------------------------------------------
// .lore.md — dedicated knowledge file
// ---------------------------------------------------------------------------

/**
 * Returns true if a `.lore.md` file exists in the project root.
 */
export function loreFileExists(projectPath: string): boolean {
  if (isHostedMode()) return false;
  return existsSync(join(projectPath, LORE_FILE));
}

/**
 * Export current knowledge entries to `.lore.md` in the project root.
 * The entire file is lore-owned — no section markers, no content to preserve.
 *
 * Skips the write if the content hash matches the cached hash (DB state
 * unchanged since last export), avoiding unnecessary filesystem writes
 * and mtime bumps.
 */
export function exportLoreFile(
  projectPath: string,
  entries?: ltm.KnowledgeEntry[],
): void {
  if (isHostedMode()) return;
  if (!loreConfig().loreFile.enabled) return;

  const sectionBody = buildSection(projectPath, entries);
  const content = `${LORE_FILE_HEADER}\n${sectionBody}`;
  const contentHash = hashSection(content);

  const fp = join(projectPath, LORE_FILE);

  // Skip write if content hash matches cached hash (DB state unchanged).
  const cached = getCache(fp);
  if (cached && cached.hash === contentHash) {
    return;
  }

  // Content changed — write and update cache.
  // Wrap in try-catch to silently handle ENOENT (project dir deleted/renamed
  // mid-session). Other FS errors (EACCES, EIO) still propagate.
  try {
    writeFileSync(fp, content, "utf8");
    const { mtimeMs } = statSync(fp);
    setCache(fp, { mtimeMs, hash: contentHash });
  } catch (e: unknown) {
    if ((e as NodeJS.ErrnoException).code === "ENOENT") return;
    throw e;
  }
}

/**
 * Returns true if `.lore.md` needs to be imported:
 * - File exists and its content differs from what lore would currently produce.
 *
 * Uses an mtime + content-hash cache to skip the expensive buildSection()
 * call when the file hasn't been touched since we last processed it.
 */
export function shouldImportLoreFile(projectPath: string): boolean {
  if (isHostedMode()) return false;

  const fp = join(projectPath, LORE_FILE);
  if (!existsSync(fp)) return false;

  // Fast path: if mtime hasn't changed since last processing, skip entirely.
  const { mtimeMs } = statSync(fp);
  const cached = getCache(fp);
  if (cached && cached.mtimeMs === mtimeMs) {
    return false;
  }

  // Slow path: mtime changed (or first check) — read file and compare content.
  const fileContent = readFileSync(fp, "utf8");
  const fileHash = hashSection(fileContent);
  const expected = `${LORE_FILE_HEADER}\n${buildSection(projectPath)}`;
  const expectedHash = hashSection(expected);

  if (fileHash === expectedHash) {
    // File matches DB — update cache so next call fast-paths.
    setCache(fp, { mtimeMs, hash: fileHash });
    return false;
  }

  return true;
}

/**
 * Import knowledge entries from `.lore.md` into the local DB.
 * Parses the full file content (no section markers to split on).
 *
 * After a successful import, updates the file cache so that
 * `shouldImportLoreFile()` fast-paths on the next check — the file
 * content hasn't changed, only the DB was updated to match it.
 */
export function importLoreFile(projectPath: string): void {
  if (isHostedMode()) return;

  const fp = join(projectPath, LORE_FILE);
  if (!existsSync(fp)) return;

  const fileContent = readFileSync(fp, "utf8");
  const fileEntries = parseEntriesFromSection(fileContent);
  if (!fileEntries.length) return;

  _importEntries(fileEntries, projectPath);

  // Update cache: DB now matches the file, so shouldImportLoreFile() can
  // fast-path on the next check.  We re-stat after import because the file
  // hasn't changed — only the DB was updated to match it.
  try {
    const { mtimeMs } = statSync(fp);
    setCache(fp, { mtimeMs, hash: hashSection(fileContent) });
  } catch {
    // stat failure is non-fatal — worst case we re-import next time
  }
}

/**
 * Import knowledge entries from a `.lore.md` file at `sourcePath`,
 * attributed to `targetProjectPath`'s project.
 *
 * Used for monorepo workspace imports: sub-project knowledge should be
 * visible in the parent project's session, so entries are created under
 * the target project's ID rather than the source directory's.
 *
 * The source `.lore.md` is read-only — Lore does not write back to it.
 */
export function importLoreFileAs(
  sourcePath: string,
  targetProjectPath: string,
): void {
  if (isHostedMode()) return;

  const fp = join(sourcePath, LORE_FILE);
  if (!existsSync(fp)) return;

  // Fast path: skip re-import when the source file hasn't changed since
  // the last import (mtime check, then content hash verification).
  try {
    const { mtimeMs } = statSync(fp);
    const cached = getCache(fp);
    if (cached && cached.mtimeMs === mtimeMs) return;
  } catch {
    // stat failure — proceed with import
  }

  const fileContent = readFileSync(fp, "utf8");
  const fileEntries = parseEntriesFromSection(fileContent);
  if (!fileEntries.length) return;

  _importEntries(fileEntries, targetProjectPath);

  // Update cache so subsequent calls fast-path.
  try {
    const { mtimeMs } = statSync(fp);
    setCache(fp, { mtimeMs, hash: hashSection(fileContent) });
  } catch {
    // stat failure is non-fatal
  }
}
